import React from 'react';
import { Dumbbell, Users, Clock, MapPin, Phone, Mail, Star, Award, Zap, Heart, ArrowRight, CheckCircle } from 'lucide-react';

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-gray-50" style={{fontFamily: 'system-ui, -apple-system, "SF Pro Display", "Segoe UI", Roboto, Ubuntu, sans-serif'}}>
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-2xl shadow-lg">
                <Dumbbell className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Sunshine Gym</h1>
                <p className="text-gray-500 text-sm font-medium">Transform Your Life</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <nav className="hidden md:flex space-x-6">
                <a href="#about" className="text-gray-600 hover:text-gray-900 font-medium transition-colors">About</a>
                <a href="#plans" className="text-gray-600 hover:text-gray-900 font-medium transition-colors">Plans</a>
                <a href="#contact" className="text-gray-600 hover:text-gray-900 font-medium transition-colors">Contact</a>
              </nav>
              <a href="/dashboard" className="bg-gray-900 text-white px-6 py-2.5 rounded-xl font-semibold transition-all duration-200 hover:bg-gray-800 hover:shadow-lg">
                Get Started
              </a>

            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-500/10 to-yellow-500/10"></div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="inline-flex items-center px-4 py-2 bg-white/10 rounded-full text-sm font-semibold">
                  <Star className="h-4 w-4 text-yellow-400 mr-2" />
                  #1 Rated Gym in the City
                </div>
                <h2 className="text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
                  Transform Your
                  <span className="bg-gradient-to-r from-orange-400 to-yellow-500 bg-clip-text text-transparent"> Body</span>
                </h2>
                <p className="text-xl text-gray-300 font-medium leading-relaxed">
                  Join Sunshine Gym and discover your strength. State-of-the-art equipment, expert trainers, and a community that supports your fitness journey every step of the way.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="group bg-gradient-to-r from-orange-500 to-yellow-500 text-white px-8 py-4 rounded-xl font-bold transition-all duration-300 hover:shadow-xl hover:shadow-orange-500/25 hover:scale-105">
                  <span className="flex items-center justify-center">
                    Start Your Journey
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </button>
                <button className="bg-white/10 backdrop-blur text-white px-8 py-4 rounded-xl font-semibold border border-white/20 hover:bg-white/20 transition-all duration-200">
                  Take a Virtual Tour
                </button>
              </div>

              <div className="grid grid-cols-3 gap-6 pt-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-white">500+</div>
                  <div className="text-gray-400 text-sm font-medium">Happy Members</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-white">5+</div>
                  <div className="text-gray-400 text-sm font-medium">Years Experience</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-white">24/6</div>
                  <div className="text-gray-400 text-sm font-medium">Operating Hours</div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative z-10 bg-white/10 backdrop-blur rounded-3xl p-8 border border-white/20">
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="w-20 h-20 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Dumbbell className="h-10 w-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">Start Today</h3>
                    <p className="text-gray-300">Get your first week free!</p>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center text-white">
                      <CheckCircle className="h-5 w-5 text-green-400 mr-3" />
                      <span className="font-medium">No joining fees</span>
                    </div>
                    <div className="flex items-center text-white">
                      <CheckCircle className="h-5 w-5 text-green-400 mr-3" />
                      <span className="font-medium">Free fitness assessment</span>
                    </div>
                    <div className="flex items-center text-white">
                      <CheckCircle className="h-5 w-5 text-green-400 mr-3" />
                      <span className="font-medium">Personal training session</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 tracking-tight mb-4">
              Why Choose Sunshine Gym?
            </h2>
            <p className="text-xl text-gray-600 font-medium max-w-2xl mx-auto">
              We're more than just a gym. We're your partner in achieving a healthier, stronger you.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="group bg-gray-50 rounded-2xl p-8 hover:bg-white hover:shadow-lg transition-all duration-300 border border-gray-100">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Dumbbell className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Modern Equipment</h3>
              <p className="text-gray-600 font-medium">Latest cardio and strength training equipment from top brands, maintained to perfection.</p>
            </div>

            <div className="group bg-gray-50 rounded-2xl p-8 hover:bg-white hover:shadow-lg transition-all duration-300 border border-gray-100">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Users className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Expert Trainers</h3>
              <p className="text-gray-600 font-medium">Certified professionals who guide you safely toward your fitness goals with personalized attention.</p>
            </div>

            <div className="group bg-gray-50 rounded-2xl p-8 hover:bg-white hover:shadow-lg transition-all duration-300 border border-gray-100">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Heart className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Wellness Focus</h3>
              <p className="text-gray-600 font-medium">Yoga, meditation, and holistic wellness programs for mind-body transformation.</p>
            </div>

            <div className="group bg-gray-50 rounded-2xl p-8 hover:bg-white hover:shadow-lg transition-all duration-300 border border-gray-100">
              <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Zap className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">High Energy</h3>
              <p className="text-gray-600 font-medium">Dynamic group classes, energizing music, and a motivating atmosphere to keep you going.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Membership Plans */}
      <section id="plans" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 tracking-tight mb-4">
              Choose Your Plan
            </h2>
            <p className="text-xl text-gray-600 font-medium">
              Flexible membership options designed to fit your lifestyle and budget
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Basic Plan */}
            <div className="bg-white rounded-3xl p-8 border border-gray-200 hover:shadow-xl transition-all duration-300">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-2xl mb-4">
                  <Award className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Basic</h3>
                <div className="text-4xl font-bold text-gray-900">₹999<span className="text-lg text-gray-500 font-medium">/month</span></div>
              </div>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span className="text-gray-700 font-medium">Access to gym equipment</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span className="text-gray-700 font-medium">Locker facility</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span className="text-gray-700 font-medium">Basic fitness assessment</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span className="text-gray-700 font-medium">Monday to Saturday access</span>
                </li>
              </ul>
              
              <button className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-xl font-semibold hover:bg-gray-200 transition-colors duration-200">
                Choose Basic
              </button>
            </div>

            {/* Premium Plan */}
            <div className="bg-white rounded-3xl p-8 border-2 border-orange-400 hover:shadow-xl transition-all duration-300 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-gradient-to-r from-orange-500 to-yellow-500 text-white px-4 py-1 rounded-full text-sm font-bold">
                  MOST POPULAR
                </span>
              </div>
              
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-100 to-yellow-100 rounded-2xl mb-4">
                  <Star className="h-8 w-8 text-orange-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Premium</h3>
                <div className="text-4xl font-bold text-gray-900">₹1,499<span className="text-lg text-gray-500 font-medium">/month</span></div>
              </div>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span className="text-gray-700 font-medium">All Basic features</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span className="text-gray-700 font-medium">Group fitness classes</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span className="text-gray-700 font-medium">Steam & shower access</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span className="text-gray-700 font-medium">Yoga studio access</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span className="text-gray-700 font-medium">7 days a week access</span>
                </li>
              </ul>
              
              <button className="w-full bg-gradient-to-r from-orange-500 to-yellow-500 text-white py-3 px-6 rounded-xl font-semibold hover:shadow-lg hover:shadow-orange-500/25 transition-all duration-200">
                Choose Premium
              </button>
            </div>

            {/* VIP Plan */}
            <div className="bg-white rounded-3xl p-8 border border-gray-200 hover:shadow-xl transition-all duration-300">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-2xl mb-4">
                  <Zap className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">VIP</h3>
                <div className="text-4xl font-bold text-gray-900">₹2,499<span className="text-lg text-gray-500 font-medium">/month</span></div>
              </div>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span className="text-gray-700 font-medium">All Premium features</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span className="text-gray-700 font-medium">Personal training sessions</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span className="text-gray-700 font-medium">Nutrition consultation</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span className="text-gray-700 font-medium">Priority equipment access</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span className="text-gray-700 font-medium">Guest pass privileges</span>
                </li>
              </ul>
              
              <button className="w-full bg-gray-900 text-white py-3 px-6 rounded-xl font-semibold hover:bg-gray-800 transition-colors duration-200">
                Choose VIP
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 tracking-tight mb-4">
              Visit Us Today
            </h2>
            <p className="text-xl text-gray-600 font-medium">
              Come see what makes Sunshine Gym the premier fitness destination
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mb-6">
                <MapPin className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Location</h3>
              <p className="text-gray-600 font-medium mb-2">123, Main Road</p>
              <p className="text-gray-600 font-medium mb-2">Tiruvannamalai, Tamil Nadu</p>
              <p className="text-gray-600 font-medium">India - 606001</p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mb-6">
                <Phone className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Contact</h3>
              <p className="text-gray-600 font-medium mb-2">Phone: +91-9876543210</p>
              <p className="text-gray-600 font-medium mb-2">Email: info@sunshinegym.in</p>
              <p className="text-gray-600 font-medium">WhatsApp: +91-9876543210</p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mb-6">
                <Clock className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Hours</h3>
              <p className="text-gray-600 font-medium mb-2">Mon - Sat: 5:00 AM - 10:00 PM</p>
              <p className="text-gray-600 font-medium mb-2">Sunday: 7:00 AM - 1:00 PM</p>
              <p className="text-gray-600 font-medium">Holidays: Check schedule</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold tracking-tight mb-4">
            Ready to Start Your Fitness Journey?
          </h2>
          <p className="text-xl text-gray-300 font-medium mb-8">
            Join hundreds of members who have transformed their lives at Sunshine Gym
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-gradient-to-r from-orange-500 to-yellow-500 text-white px-10 py-4 rounded-xl font-bold transition-all duration-300 hover:shadow-xl hover:shadow-orange-500/25 hover:scale-105">
              Join Now - Get 1 Week Free
            </button>
            <button className="bg-white/10 backdrop-blur text-white px-10 py-4 rounded-xl font-semibold border border-white/20 hover:bg-white/20 transition-all duration-200">
              Schedule a Tour
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      {/* Admin Login link at the very bottom above the footer */}
      <div className="flex justify-center mb-4 mt-12">
        <a href="/login" className="text-gray-500 hover:text-gray-900 font-medium transition-colors duration-200 text-sm underline">Admin Login</a>
      </div>
      <footer className="bg-white border-t border-gray-100 py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-4 mb-4 md:mb-0">
              <div className="p-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-xl">
                <Dumbbell className="h-6 w-6 text-white" />
              </div>
              <div>
                <span className="text-xl font-bold text-gray-900">Sunshine Gym</span>
                <p className="text-gray-500 text-sm">Transform Your Life</p>
              </div>
            </div>
            <div className="text-center md:text-right">
              <p className="text-gray-500 font-medium">&copy; {new Date().getFullYear()} Sunshine Gym. All rights reserved.</p>
              <p className="text-gray-400 text-sm">Made with ❤️ for fitness enthusiasts</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;